import React from 'react'
import { deburr, kebabCase, replace, delay } from 'lodash'
import { Box } from '@hub/box'
import { Portal } from '@hub/portal'
import { Link } from '@hub/link'
import { LinkItem } from '../types'
import analyticsUtils from '../../../helpers/analytics-utils'
import trackEvent from '../../../helpers/analytics-utils/track-event'
import { MegaMenu } from './megamenu'
import { MainNavItemWrapper } from './main-nav-item-wrapper'
import { SimpleMenu } from './simple-menu'

interface MegaMenuNavItemProps {
  item: LinkItem
  index: number
  megaMenuPortalRef?: React.RefObject<HTMLElement | null>
  openId: string
  setOpenId: (id: string) => void
}

export const MegaMenuNavItem: React.FC<MegaMenuNavItemProps> = ({
  item,
  index,
  megaMenuPortalRef,
  openId,
  setOpenId,
}) => {
  const bounceRef = React.useRef(0)
  const focusRef = React.useRef(false)
  const megamenuFocusRef = React.useRef(false)
  const itemId = kebabCase(replace(deburr(item.label), /([^A-Za-z])/, ''))
  const isOpen = openId === itemId

  // For some reason I can't figure out when first loaded by SSR the event is triggered twice
  // To get around this I wanted to use _.debounce but because we still need to stop propegation on that second call
  // I'm tracking time myself to prevent the second call from happening
  const open: React.MouseEventHandler = event => {
    const MINIMUM_TIME_BETWEEN_EVENTS = 300
    const bounced =
      event.timeStamp - bounceRef.current < MINIMUM_TIME_BETWEEN_EVENTS
    if (isOpen && !bounced) {
      bounceRef.current = event.timeStamp
      setOpenId('')
    } else {
      event.preventDefault()
      event.stopPropagation()
      bounceRef.current = event.timeStamp
      setOpenId(itemId)
    }
  }
  const close = (): void => {
    setOpenId('')
  }

  const handleKeyDown: React.KeyboardEventHandler = event => {
    if (event.key === 'Enter') {
      focusRef.current = false
      megamenuFocusRef.current = true
    }
  }

  const [liRef, setLiRef] = React.useState<HTMLLIElement | null>(null)

  const handleMouseMove = (): void => {
    if (itemId !== openId) {
      setOpenId(itemId)
    }
  }

  return (
    <MainNavItemWrapper
      isDisplayedHorizontally
      isOpen={isOpen}
      ref={listItem => {
        if (listItem && focusRef.current) {
          listItem.querySelector('a')?.focus()
          focusRef.current = false
        }
        if (listItem && liRef === null) {
          setLiRef(listItem)
        }
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={() => (focusRef.current = true)}
      onBlur={() => {
        focusRef.current = false
      }}
      onKeyDown={handleKeyDown}
    >
      <Link
        href={item.url}
        hasUnderline={false}
        aria-expanded={isOpen}
        aria-controls={`${itemId}`}
        sx={{
          height: 'size-full',
          display: 'inline-flex',
          alignItems: 'center',
          _hover: {
            textDecoration: 'none',
          },
        }}
        onClick={e => {
          trackEvent(
            analyticsUtils.categories.UNIVERSAL_HEADER,
            analyticsUtils.actions.UNIVERSAL_HEADER_NAV_LINK_CLICK,
            item.label
          )
          open(e)
        }}
      >
        {item.label}
      </Link>
      <Portal containerRef={megaMenuPortalRef}>
        {isOpen &&
          (item.simpleMenuItems ? (
            <SimpleMenu
              itemId={itemId}
              itemLabel={item.label}
              index={index}
              menuItems={item.simpleMenuItems}
              liComponentRef={liRef}
              megamenuFocusRef={megamenuFocusRef}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                right: '50%',
                top: '100%',
                width: '100%',
                marginRight: '-50%',
                marginLeft: '-50%',
                zIndex: 'dropdown',
              }}
              role="none"
              ref={(box: HTMLElement) => {
                if (box && megamenuFocusRef.current) {
                  const DELAY_FOR_MAKING_SURE_FOCUS_IS_POSSIBLE = 50
                  delay(
                    () => box.querySelector('a')?.focus(),
                    DELAY_FOR_MAKING_SURE_FOCUS_IS_POSSIBLE
                  )
                  megamenuFocusRef.current = false
                }
              }}
            >
              <Box
                sx={{
                  marginTop: '1px',
                  backgroundColor: 'white',
                }}
              >
                <MegaMenu
                  itemId={itemId}
                  itemLabel={item.label}
                  megaMenuItems={item.megaMenuItems}
                />
              </Box>
            </Box>
          ))}
      </Portal>
    </MainNavItemWrapper>
  )
}
